<template>
  <div class='widget-W0159V0001'>
    <section aria-label="StudentPulse check-in" class="studentpulse-widget">
      <header class="studentpulse-widget__header">
        <h2>StudentPulse check-in</h2>
        <p v-if="userProfile !== 'child'">Vælg det barn, der skal besvare check-in.</p>
      </header>

      <p v-if="unavailableMessage" class="studentpulse-widget__message" role="status">
        {{ unavailableMessage }}
      </p>

      <p v-else-if="loading" class="studentpulse-widget__message" role="status">Henter børn...</p>

      <div v-else-if="errorMessage" class="studentpulse-widget__error" role="alert">
        <p>{{ errorMessage }}</p>
        <button class="studentpulse-widget__secondary" type="button" @click="reload">Prøv igen</button>
      </div>

      <p v-else-if="children.length === 0" class="studentpulse-widget__message" role="status">
        Der er ingen børn at vise.
      </p>

      <form
        v-else
        :action="`${normalizedApiUri}/check-ins`"
        method="post"
        target="_blank"
        @submit.prevent="openCheckin"
      >
        <fieldset v-show="userProfile !== 'child'">
          <legend class="studentpulse-widget__legend">Vælg barn</legend>

          <label
            v-for="child in children"
            :key="child.id"
            :class="{
              'studentpulse-widget__choice--disabled': !child.available,
              'studentpulse-widget__choice--selected': selectedChildId === child.id,
            }"
            class="studentpulse-widget__choice"
          >
            <input
              v-model="selectedChildId"
              :disabled="!child.available"
              :value="child.id"
              name="childId"
              type="radio"
            >
            <span>
                        <strong>{{ child.name }}</strong>
                        <small>{{ child.main_group }}</small>
                        <small v-if="!child.available" class="studentpulse-widget__unavailable">
                            Ingen check-in tilgængelig
                        </small>
                    </span>
          </label>
        </fieldset>

        <input :value="aulaToken" name="AulaToken" type="hidden">
        <input :value="userProfile" name="userProfile" type="hidden">
        <input :value="sessionUUID" name="sessionUUID" type="hidden">
        <input
          v-for="institution in activeInstitutionFilter"
          :key="`institution-${institution}`"
          :value="institution"
          name="institutionFilter[]"
          type="hidden"
        >
        <input
          v-for="childId in activeChildFilter"
          :key="`child-${childId}`"
          :value="childId"
          name="childFilter[]"
          type="hidden"
        >

        <button
          :disabled="!selectedChildId || tokenLoading || !aulaToken"
          class="studentpulse-widget__primary"
          type="submit"
        >
          {{ tokenLoading ? 'Gør klar...' : 'Åbn check-in' }}
        </button>
      </form>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    axios: {
      type: Function,
      required: true,
    },
    apiUri: {
      type: String,
      default: 'https://education.studentpulse.io/api/sso/aula',
    },
    getAulaToken: {
      default: null,
      validator(value) {
        return value === null || typeof value === 'function';
      },
    },
    sessionUUID: {
      type: String,
      required: true,
    },
    userProfile: {
      type: String,
      required: true,
    },
    childFilter: {
      default() {
        return [];
      },
      validator(value) {
        return value === null || Array.isArray(value);
      },
    },
    institutionFilter: {
      default() {
        return [];
      },
      validator(value) {
        return value === null || Array.isArray(value);
      },
    },
  },

  data() {
    return {
      aulaToken: null,
      children: [],
      errorMessage: '',
      loading: false,
      selectedChildId: '',
      tokenLoading: false,
    };
  },

  computed: {
    activeChildFilter() {
      return Array.isArray(this.childFilter)
        ? this.childFilter.map(value => String(value).trim()).filter(Boolean)
        : [];
    },
    activeInstitutionFilter() {
      return Array.isArray(this.institutionFilter)
        ? this.institutionFilter.map(value => String(value).trim()).filter(Boolean)
        : [];
    },
    normalizedApiUri() {
      return this.apiUri.replace(/\/$/, '');
    },
    unavailableMessage() {
      if (!['guardian', 'child'].includes(this.userProfile)) {
        return 'Denne check-in er kun tilgængelig for værger og elever.';
      }

      if (typeof this.getAulaToken !== 'function') {
        return 'Denne widget skal konfigureres som en sikker widget i Aula.';
      }

      if (!this.normalizedApiUri) {
        return 'Widgetten mangler en API-adresse. Angiv apiUri i Aula.';
      }

      if (this.activeInstitutionFilter.length !== 1) {
        return 'Vælg én institution i Aula for at åbne check-in.';
      }

      return '';
    },
  },

  mounted() {
    this.reload();
  },

  methods: {
    async loadChildren() {
      if (this.unavailableMessage || !this.aulaToken) {
        return;
      }

      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await this.axios.get(`${this.normalizedApiUri}/children`, {
          headers: {
            Authorization: `Bearer ${this.aulaToken}`,
          },
          params: {
            childFilter: this.activeChildFilter,
            institutionFilter: this.activeInstitutionFilter,
            sessionUUID: this.sessionUUID,
            userProfile: this.userProfile,
          },
          timeout: 10000,
        });

        const responseData = response && response.data !== undefined ? response.data : response;
        const children = Array.isArray(responseData) ? responseData : responseData && responseData.data;

        this.children = Array.isArray(children) ? children : [];
        const availableChildren = this.children.filter(child => child.available);

        if (availableChildren.length === 1) {
          this.selectedChildId = availableChildren[0].id;
        }
      } catch (error) {
        const status = error.response && error.response.status;
        const reason = status || error.message;

        this.errorMessage = `Børnene kunne ikke hentes (${reason}). Prøv igen.`;
      } finally {
        this.loading = false;
      }
    },

    async reload() {
      this.aulaToken = null;
      this.children = [];
      this.errorMessage = '';
      this.selectedChildId = '';

      if (this.unavailableMessage) {
        return;
      }

      await this.requestAulaToken();
      await this.loadChildren();
    },

    requestAulaToken() {
      this.tokenLoading = true;

      try {
        this.aulaToken = this.getAulaToken();
      } catch (error) {
        this.aulaToken = null;
        this.errorMessage = 'Aula-login kunne ikke bekræftes. Prøv igen.';
      } finally {
        this.tokenLoading = false;
      }
    },

    async openCheckin(event) {
      const form = event.currentTarget;

      await this.requestAulaToken();

      if (!this.aulaToken) {
        return;
      }

      await this.$nextTick();
      form.submit();
    },
  },
};
</script>

<style lang="scss">
.widget-W0159V0001 {
  .studentpulse-widget {
    box-sizing: border-box;
    color: #19425f;
    font-family: Lato, Arial, sans-serif;
    font-size: 0.9375rem;
    line-height: 1.45;
    max-width: 44rem;
    padding: 1rem;
    width: 100%;
  }

  .studentpulse-widget__header h2 {
    font-size: 1.375rem;
    font-weight: 900;
    line-height: 1.2;
    margin: 0 0 0.25rem;
  }

  .studentpulse-widget__header p,
  .studentpulse-widget__message,
  .studentpulse-widget__error p {
    margin: 0 0 1rem;
  }

  .studentpulse-widget__message {
    color: #347da7;
  }

  .studentpulse-widget__error {
    background: #f6f7f8;
    border-left: 0.25rem solid #b50050;
    color: #b50050;
    padding: 1rem;
  }

  .studentpulse-widget fieldset {
    border: 0;
    margin: 1rem 0;
    padding: 0;
  }

  .studentpulse-widget__legend {
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .studentpulse-widget__choice {
    align-items: flex-start;
    background: #fff;
    border: 1px solid #d9e3e9;
    border-radius: 0.25rem;
    cursor: pointer;
    display: flex;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    padding: 0.75rem;
    transition: background-color 120ms ease, border-color 120ms ease;
  }

  .studentpulse-widget__choice:hover,
  .studentpulse-widget__choice--selected {
    background: #f6f7f8;
    border-color: #347da7;
  }

  .studentpulse-widget__choice:focus-within {
    outline: 0.1875rem solid rgba(52, 125, 167, 0.35);
    outline-offset: 0.125rem;
  }

  .studentpulse-widget__choice input {
    accent-color: #347da7;
    flex: 0 0 auto;
    margin-top: 0.2rem;
  }

  .studentpulse-widget__choice span,
  .studentpulse-widget__choice small {
    display: block;
  }

  .studentpulse-widget__choice small {
    color: #347da7;
    margin-top: 0.15rem;
  }

  .studentpulse-widget__choice--disabled {
    background: #e9eef1;
    border-color: #d9dfe3;
    color: #7b8d99;
    cursor: not-allowed;
  }

  .studentpulse-widget__choice--disabled:hover {
    background: #e9eef1;
    border-color: #d9dfe3;
  }

  .studentpulse-widget__unavailable,
  .studentpulse-widget__error {
    color: #b50050;
  }

  .studentpulse-widget__primary,
  .studentpulse-widget__secondary {
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    min-height: 2.75rem;
    padding: 0.65rem 1rem;
    text-transform: uppercase;
  }

  .studentpulse-widget__primary {
    background: #549ec7;
    color: #fff;
    width: 100%;
  }

  .studentpulse-widget__primary:hover:not(:disabled) {
    background: #347da7;
  }

  .studentpulse-widget__primary:focus-visible,
  .studentpulse-widget__secondary:focus-visible {
    outline: 0.1875rem solid rgba(52, 125, 167, 0.35);
    outline-offset: 0.125rem;
  }

  .studentpulse-widget__primary:disabled {
    background: #fff;
    border-color: #d9dfe3;
    color: #9aa8b2;
    cursor: not-allowed;
  }

  .studentpulse-widget__secondary {
    background: #fff;
    border-color: #347da7;
    color: #347da7;
  }

  .studentpulse-widget__secondary:hover {
    background: #f6f7f8;
  }

  @media (max-width: 30rem) {
    .studentpulse-widget {
      padding: 0.75rem;
    }

    .studentpulse-widget__choice {
      padding: 0.75rem 0.625rem;
    }
  }
}
</style>
